<template>
  <div class="main-layout">
    <!-- Sidebar (navegación lateral) -->
    <aside aria-label="Menú de navegación">
      <AppSidebar v-model="sidebarOpen" />
    </aside>

    <!-- Contenedor principal -->
    <div class="main-container" :class="{ 'sidebar-collapsed': !sidebarOpen }">
      <!-- Top Bar -->
      <header>
        <AppHeader :sidebar-open="sidebarOpen" @toggle-sidebar="sidebarOpen = !sidebarOpen" />
      </header>

      <!-- Banner de suscripción (EmisorAdmin) -->
      <SuscripcionBanner />

      <!-- Banner de conexión a internet -->
      <v-slide-y-transition>
        <v-banner
          v-if="!isOnline"
          color="error"
          icon="mdi-wifi-off"
          density="compact"
          class="network-banner"
        >
          <template #text>
            Sin conexión a internet. Verifica tu red e intenta de nuevo.
          </template>
        </v-banner>
      </v-slide-y-transition>
      <v-slide-y-transition>
        <v-banner
          v-if="isOnline && wasOffline"
          color="success"
          icon="mdi-wifi-check"
          density="compact"
          class="network-banner"
        >
          <template #text>
            Conexión restaurada.
          </template>
        </v-banner>
      </v-slide-y-transition>

      <!-- Contenido principal -->
      <main class="main-content" id="main-content">
        <div class="content-wrapper">
          <Breadcrumbs />
          <slot />
        </div>
      </main>
    </div>

  </div>
</template>

<script>
import { ref } from 'vue'
import AppSidebar from './AppSidebar.vue'
import AppHeader from './AppHeader.vue'
import SuscripcionBanner from './SuscripcionBanner.vue'
import Breadcrumbs from '../shared/Breadcrumbs.vue'
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
import { useNetworkStatus } from '@/composables/useNetworkStatus'

export default {
  name: 'MainLayout',
  components: {
    AppSidebar,
    AppHeader,
    SuscripcionBanner,
    Breadcrumbs
  },
  setup() {
    const sidebarOpen = ref(true)

    // Atajos de teclado globales (Alt+N, Alt+C, Alt+H, etc.)
    useKeyboardShortcuts()

    // Estado de conexión a internet
    const { isOnline, wasOffline } = useNetworkStatus()

    return {
      sidebarOpen,
      isOnline,
      wasOffline
    }
  }
}
</script>

<style scoped>
.main-layout {
  display: flex;
  min-height: 100vh;
  background-color: #F8FAFC;
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 240px;
  transition: margin-left 0.3s ease, width 0.3s ease;
  height: 100vh;
  overflow: hidden;
  width: calc(100% - 240px);
  min-width: 0;
}

.main-container.sidebar-collapsed {
  margin-left: 0;
  width: 100%;
}

.main-content {
  flex: 1;
  background-color: #F8FAFC;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  height: 0; /* Forzar que el contenedor respete el flex y permita scroll */
}

.content-wrapper {
  padding: 20px 24px 16px 24px;
  min-height: 100%;
}

@media (max-width: 960px) {
  .main-container {
    margin-left: 0;
    width: 100%;
  }

  .content-wrapper {
    padding: 12px 16px;
  }
}

.network-banner {
  flex: none !important;
  min-height: auto !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  flex-shrink: 0;
}

@media (max-width: 360px) {
  .content-wrapper {
    padding: 8px 12px;
  }
}
</style>

<!-- Estilos globales para Dark Mode - Paleta Slate Moderna -->
<style>
/* Dark mode: Fondo oscuro para el layout principal */
.v-theme--dark .main-layout,
.v-theme--darkTheme .main-layout {
  background-color: #0F172A;
}

.v-theme--dark .main-content,
.v-theme--darkTheme .main-content {
  background-color: #0F172A;
}

/* Dark mode: Asegurar que el texto en el contenido sea legible */
.v-theme--dark .content-wrapper,
.v-theme--darkTheme .content-wrapper {
  color: #E0E0E0;
}

/* Dark mode: Títulos y subtítulos */
.v-theme--dark .content-wrapper h1,
.v-theme--dark .content-wrapper h2,
.v-theme--dark .content-wrapper h3,
.v-theme--darkTheme .content-wrapper h1,
.v-theme--darkTheme .content-wrapper h2,
.v-theme--darkTheme .content-wrapper h3 {
  color: #FFFFFF;
}

/* Dark mode: Texto secundario */
.v-theme--dark .content-wrapper .text-grey,
.v-theme--darkTheme .content-wrapper .text-grey {
  color: #BDBDBD !important;
}
</style>
